import React, { useMemo } from "react";
import PropTypes from "prop-types";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useNavigate } from "react-router-dom";

import { ArticleBody } from "./HelpMarkdownStyled";

// Articles cross-reference each other by slug, e.g.
// [Compress & Submit](submit-to-va/compress-and-submit). Those are
// in-app moves, so they must not reload the browser; anything with a
// scheme or a leading slash is treated as a genuine external link.
const isArticleSlug = (href) =>
  typeof href === "string" &&
  href.length > 0 &&
  !href.startsWith("#") &&
  !href.startsWith("/") &&
  !/^[a-z][a-z0-9+.-]*:/i.test(href);

// Headings get ids so search results and the on-page contents can jump
// straight to the relevant part of a long article. Must match the
// anchor ids the backend records when it loads the article.
const headingId = (children) => {
  const text = React.Children.toArray(children)
    .map((child) => (typeof child === "string" ? child : ""))
    .join("");
  return text
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/[\s-]+/g, "-");
};

const HelpMarkdown = ({ body }) => {
  const navigate = useNavigate();

  const components = useMemo(
    () => ({
      a: ({ href, children, ...rest }) => {
        if (isArticleSlug(href)) {
          return (
            <a
              href={`/help/${href}`}
              onClick={(event) => {
                // Leave modified clicks alone so "open in new tab" works.
                if (
                  event.metaKey ||
                  event.ctrlKey ||
                  event.shiftKey ||
                  event.button !== 0
                ) {
                  return;
                }
                event.preventDefault();
                navigate(`/help/${href}`);
              }}
              {...rest}
            >
              {children}
            </a>
          );
        }
        return (
          <a href={href} target="_blank" rel="noopener noreferrer" {...rest}>
            {children}
          </a>
        );
      },
      h2: ({ children, ...rest }) => (
        <h2 id={headingId(children)} {...rest}>
          {children}
        </h2>
      ),
      h3: ({ children, ...rest }) => (
        <h3 id={headingId(children)} {...rest}>
          {children}
        </h3>
      ),
    }),
    [navigate]
  );

  if (!body) return null;

  return (
    <ArticleBody>
      <Markdown remarkPlugins={[remarkGfm]} components={components}>
        {body}
      </Markdown>
    </ArticleBody>
  );
};

HelpMarkdown.propTypes = {
  body: PropTypes.string,
};

HelpMarkdown.defaultProps = {
  body: "",
};

export default HelpMarkdown;
